import React from 'react'

import {
  type NumericOperatorType,
  type OperatorType,
  type SelectedFilterOption,
} from 'features/charts-v2/types'
import type { InheritedFilterEntry } from 'features/charts-v2/types/inherited-filters'
import { FilterOption } from 'features/filters/types'

/**
 * One row in the sidebar's filter list. 'owned' is the tile's own filter on
 * this field (rendered editable, as always); 'inherited' is a dashboard
 * global filter on this field (rendered read-only via the `inherited` prop
 * below). Both kinds can appear for the same field — that's the interleaved
 * override case — so this is a list of rows, not a map keyed by field.
 */
export type FilterCardEntry = (
  | {
      kind: 'owned'
      filter: FilterOption
      // The parent scope's selection for this field, when the parent also
      // filters it — caps this card's value popover at the parent's values.
      globalSelection?: SelectedFilterOption
      // A field the catalogue lacks: no eye toggle, since a ghosted field
      // outside the catalogue has no card to come back to. Remove only.
      synthesized?: boolean
    }
  | { kind: 'inherited'; filter: FilterOption; entry: InheritedFilterEntry }
) & {
  // Ownership accent bar — 'local' (orange) for an owned entry inside a
  // dashboard-tile context, 'global' (navy) for every inherited entry.
  // Undefined outside a dashboard context (charts/feedback/keywords), where
  // owned entries render with no bar at all.
  accent?: 'local' | 'global'
}

interface SelectedFilterOptionListProps {
  entries: FilterCardEntry[]
  updateFilterSelection: (
    field: string,
    operator: OperatorType | NumericOperatorType,
    values: string[],
  ) => void
  getFilterSelection: (field: string) => SelectedFilterOption | null
  // The ghost (eye-toggled-off) controls, bundled into one prop to keep this
  // component's own prop count down — see `use-ghost-filter-controls.ts` for
  // where these three come from. Omitted entirely hides the eye/disable
  // toggle on every owned card for this consumer.
  ghost?: {
    // Looks up a field's stashed (eye-toggled-off) definition, if any.
    getGhostSelection: (field: string) => SelectedFilterOption | null
    onToggleFilterDisabled: (field: string) => void
    onRemoveGhost: (field: string) => void
  }
  // Everything this list needs about the parent (dashboard) scope, bundled
  // into one prop to keep this component's own prop count down (mirrors the
  // `ghost`/`presentation` bundles). `sourceLabel` feeds every inherited
  // card's `inherited.sourceLabel`; `onEditSource` feeds both every
  // inherited card's `inherited.onEditSource` AND every row's `accentBar`
  // (the accent bar reads it regardless of `kind` — see `FilterCard`'s doc
  // comment on why the two are separate); `tileKind` feeds every row's
  // `accentBar` too. Omitted entirely by any caller with no parent scope
  // (same callers that omit `inherited` on `FilterControlProps`).
  parentScope?: {
    sourceLabel: string
    onEditSource?: () => void
    tileKind?: 'chart' | 'widget'
  }
  // Looks up a field's stale values (present in the stored selection but no
  // longer offered by the project), if any. Omitted entirely renders no
  // stale badge on any card — see `FilterCard`'s `stale` doc comment.
  getStaleInfo?: (
    field: string,
  ) => { values: readonly string[]; labels: Record<string, string> } | undefined
  FilterCard: React.ComponentType<{
    filter: FilterOption
    updateFilterSelection: (
      field: string,
      operator: OperatorType | NumericOperatorType,
      values: string[],
    ) => void
    getFilterSelection: (field: string) => SelectedFilterOption | null
    presentation?: {
      showBreakdownHierarchy?: boolean
      lockOperator?: boolean
      getValueLabel?: (field: string, value: string) => string | undefined
      globalSelection?: SelectedFilterOption
    }
    ghost?: {
      disabledSelection?: SelectedFilterOption
      onToggleDisabled: () => void
      onRemoveGhost: () => void
    }
    inherited?: {
      selection: SelectedFilterOption
      supersededNote: string | null
      sourceLabel: string
      onEditSource?: () => void
    }
    accentBar?: {
      accent: 'local' | 'global'
      tileKind?: 'chart' | 'widget'
      onEditSource?: () => void
    }
    stale?: {
      values: readonly string[]
      labels: Record<string, string>
    }
  }>
  // Presentation knobs bundled into one prop to keep the component's own
  // prop count down (mirrors the `ghost` bundle above). `showBreakdownHierarchy`
  // and `lockOperator` are forwarded to every rendered FilterCard — see
  // `FilterCard`'s doc comment. `maxHeight` overrides the default
  // `calc(100vh - 120px)` scroll cap for a consumer that isn't a
  // fixed-height sidebar (e.g. an inline form); omitted keeps today's
  // behavior.
  presentation?: {
    showBreakdownHierarchy?: boolean
    lockOperator?: boolean
    maxHeight?: string
    getValueLabel?: (field: string, value: string) => string | undefined
  }
}

export const SelectedFilterOptionList: React.FC<
  SelectedFilterOptionListProps
> = ({
  entries,
  updateFilterSelection,
  getFilterSelection,
  ghost,
  parentScope,
  getStaleInfo,
  FilterCard,
  presentation,
}) => {
  const {
    showBreakdownHierarchy,
    lockOperator,
    getValueLabel,
    maxHeight = 'calc(100vh - 120px)', // Leave space for header and padding
  } = presentation ?? {}
  return (
    <div
      style={{
        maxHeight,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {entries.map((entry) => (
        <FilterCard
          key={`${entry.kind}-${entry.filter.field}`}
          filter={entry.filter}
          updateFilterSelection={updateFilterSelection}
          getFilterSelection={getFilterSelection}
          presentation={{
            showBreakdownHierarchy,
            lockOperator,
            getValueLabel,
            globalSelection:
              entry.kind === 'owned' ? entry.globalSelection : undefined,
          }}
          ghost={
            entry.kind === 'owned' && !entry.synthesized && ghost
              ? {
                  disabledSelection:
                    ghost.getGhostSelection(entry.filter.field) ?? undefined,
                  onToggleDisabled: () =>
                    ghost.onToggleFilterDisabled(entry.filter.field),
                  onRemoveGhost: () => ghost.onRemoveGhost(entry.filter.field),
                }
              : undefined
          }
          inherited={
            entry.kind === 'inherited'
              ? {
                  selection: entry.entry.selection,
                  supersededNote: entry.entry.supersededNote,
                  sourceLabel: parentScope?.sourceLabel ?? '',
                  onEditSource: parentScope?.onEditSource,
                }
              : undefined
          }
          accentBar={
            entry.accent
              ? {
                  accent: entry.accent,
                  tileKind: parentScope?.tileKind,
                  onEditSource: parentScope?.onEditSource,
                }
              : undefined
          }
          stale={
            entry.kind === 'owned'
              ? getStaleInfo?.(entry.filter.field)
              : undefined
          }
        />
      ))}
    </div>
  )
}
